import React from "react";
import {
  Activity,
  ArrowUpRight,
  BookOpen,
  Building2,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  GraduationCap,
  LayoutDashboard,
  ShieldCheck,
  Users,
  WalletCards,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import AdminLayout from "../../../components/layouts/AdminLayout";
import { academicCalendarService } from "../../../services/academicCalendarService";
import { hierarchyService } from "../../../services/hierarchyService";
import { programService } from "../../../services/programService";
import { userService } from "../../../services/userService";

const reviewAreas = [
  {
    label: "Academic management",
    description: "Programmes, units, faculties and departments",
    href: "/academic-management",
    icon: BookOpen,
    tone: "text-indigo-700 bg-indigo-50 border-indigo-100",
  },
  {
    label: "Admissions & enrolment",
    description: "Applications, admissions and student intake",
    href: "/admissions",
    icon: GraduationCap,
    tone: "text-emerald-700 bg-emerald-50 border-emerald-100",
  },
  {
    label: "Assessment & Senate",
    description: "Results, approvals and examination packages",
    href: "/summative-assessment-portal",
    icon: ClipboardCheck,
    tone: "text-amber-700 bg-amber-50 border-amber-100",
  },
  {
    label: "Institution hierarchy",
    description: "Faculties, departments and sections",
    href: "/institution-hierarchy-management",
    icon: Building2,
    tone: "text-violet-700 bg-violet-50 border-violet-100",
  },
  {
    label: "Finance oversight",
    description: "Fees, collections and outstanding balances",
    href: "/finance",
    icon: WalletCards,
    tone: "text-cyan-700 bg-cyan-50 border-cyan-100",
  },
  {
    label: "Timetable & calendar",
    description: "Academic periods and institutional schedules",
    href: "/timetable-management",
    icon: CalendarDays,
    tone: "text-rose-700 bg-rose-50 border-rose-100",
  },
  {
    label: "People & records",
    description: "Institution-wide student and staff records",
    href: "/students",
    icon: Users,
    tone: "text-blue-700 bg-blue-50 border-blue-100",
  },
  {
    label: "Operations & services",
    description: "Hostel, transport, estates and facilities",
    href: "/operations",
    icon: Activity,
    tone: "text-teal-700 bg-teal-50 border-teal-100",
  },
];

const asArray = (value) => (Array.isArray(value) ? value : []);
const numberValue = (value) =>
  value === null || value === undefined || value === ""
    ? "—"
    : Number(value).toLocaleString();

const periodLabel = (period) =>
  period?.name || period?.label || period?.year_name || period?.semester_name || "Not configured";

const roleLabel = (value) => {
  const normalized = String(value || "executive leader").trim().toLowerCase();
  const labels = {
    chief_principal: "Chief Principal",
    vice_chancellor: "Vice-Chancellor",
    vc: "Vice-Chancellor",
    principal: "Principal",
  };
  return labels[normalized] || normalized.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatLevelLabel = (program) => {
  const rawLevels = [
    ...(Array.isArray(program?.levels) ? program.levels : []),
    program?.level,
    ...(Array.isArray(program?.levelUnitCounts)
      ? program.levelUnitCounts.map((item) => item?.label || item?.level)
      : []),
  ]
    .map((value) => (value && typeof value === "object" ? value.label || value.value || value.level : value))
    .filter(Boolean);

  const labels = rawLevels.map((value) => {
    const text = String(value).trim();
    const match = text.match(/(?:level[\s_-]*)?(\d+)/i);
    if (match) return `Level ${match[1]}`;

    // A few older programme records store only the qualification name. Keep
    // the dashboard's level column numeric and never show Diploma/Craft/etc.
    return text
      .replace(/\b(?:nvc|artisan|craft|diploma|certificate)\b/gi, "")
      .replace(/[()_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  });

  return [...new Set(labels.filter(Boolean))].join(", ") || "—";
};

const intakeKey = (programClass) =>
  String(programClass?.intake_code || programClass?.intake_name || programClass?.id || "")
    .trim()
    .toLowerCase();

const intakeLabel = (programClass) =>
  [programClass?.intake_name, programClass?.intake_code]
    .filter(Boolean)
    .join(" · ") || "Unnamed intake";

const ExecutiveDashboard = () => {
  const { user, profile } = useAuth();
  const [overview, setOverview] = React.useState({
    users: null,
    programs: [],
    intakes: [],
    hierarchy: null,
    years: [],
    semesters: [],
    operational: null,
  });
  const [loading, setLoading] = React.useState(true);
  const [notice, setNotice] = React.useState("");
  const [selectedIntakeKey, setSelectedIntakeKey] = React.useState("");

  const loadOverview = React.useCallback(async () => {
    // Semesters are scoped to an academic year. Loading them without a year
    // produces a rejected Supabase query, which used to show the generic
    // institutional-feed warning on every executive dashboard visit.
    const baseResults = await Promise.allSettled([
      userService.getCategorizedUserCounts(),
      programService.getAll(),
      hierarchyService.getStats(),
      academicCalendarService.getAcademicYears(),
      hierarchyService.getOperationalData(),
      programService.getIntakeFootprint(),
    ]);

    const [usersResult, programsResult, hierarchyResult, yearsResult, operationalResult, intakesResult] = baseResults;
    const years = yearsResult.status === "fulfilled" ? asArray(yearsResult.value) : [];
    const selectedYear = years.find((year) => year?.status === "active" || year?.current || year?.is_current) || years[0];
    const semestersResult = selectedYear?.id
      ? await academicCalendarService.getSemesters(selectedYear.id)
          .then((value) => ({ status: "fulfilled", value }))
          .catch((reason) => ({ status: "rejected", reason }))
      : { status: "fulfilled", value: [] };
    const operational = operationalResult.status === "fulfilled" ? operationalResult.value : null;
    const programs = programsResult.status === "fulfilled"
      ? asArray(programsResult.value)
      : asArray(operational?.programs);
    const failedCount = [...baseResults, semestersResult].filter((result) => result.status === "rejected").length;

    setOverview({
      users: usersResult.status === "fulfilled" ? usersResult.value : null,
      programs,
      intakes: intakesResult.status === "fulfilled" ? asArray(intakesResult.value) : [],
      hierarchy: hierarchyResult.status === "fulfilled" ? hierarchyResult.value : null,
      years,
      semesters: semestersResult.status === "fulfilled" ? asArray(semestersResult.value) : [],
      operational,
    });
    setNotice(
      failedCount
        ? "Some institutional feeds could not be loaded. The available figures are still shown."
        : "",
    );
    setLoading(false);
  }, []);

  React.useEffect(() => {
    void loadOverview();
  }, [loadOverview]);

  const intakeOptions = React.useMemo(() => {
    const configured = overview.intakes.filter((programClass) => programClass?.is_active !== false);
    const source = configured.length ? configured : overview.intakes;
    const unique = new Map();

    source.forEach((programClass) => {
      const key = intakeKey(programClass);
      if (!key || unique.has(key)) return;
      unique.set(key, {
        key,
        label: intakeLabel(programClass),
        startDate: programClass?.start_date || "",
      });
    });

    return [...unique.values()].sort((a, b) => {
      const aDate = Date.parse(a.startDate) || 0;
      const bDate = Date.parse(b.startDate) || 0;
      return bDate - aDate || a.label.localeCompare(b.label);
    });
  }, [overview.intakes]);

  React.useEffect(() => {
    if (!intakeOptions.length) {
      if (selectedIntakeKey) setSelectedIntakeKey("");
      return;
    }
    if (!intakeOptions.some((option) => option.key === selectedIntakeKey)) {
      setSelectedIntakeKey(intakeOptions[0].key);
    }
  }, [intakeOptions, selectedIntakeKey]);

  const fullName = profile?.full_name || user?.user_metadata?.full_name || "Institutional Leader";
  const title = roleLabel(profile?.primary_role || user?.user_metadata?.role);
  const users = overview.users || {};
  const students = users.students ?? overview.operational?.students?.length;
  const staff = users.academic !== undefined || users.staff !== undefined
    ? Number(users.academic || 0) + Number(users.staff || 0) + Number(users.admins || 0)
    : overview.operational?.staff?.length;
  const programs = overview.programs.filter((program) => program?.is_active !== false);
  const activeYear = overview.years.find((year) => year?.status === "active" || year?.current || year?.is_current);
  const activeSemester = overview.semesters.find((semester) => semester?.status === "active" || semester?.current || semester?.is_current);
  const applications = overview.operational?.applications?.length;
  const selectedIntake = intakeOptions.find((option) => option.key === selectedIntakeKey);
  const programById = new Map(programs.map((program) => [String(program.id), program]));
  const intakeProgramRows = selectedIntakeKey
    ? overview.intakes
        .filter((programClass) => intakeKey(programClass) === selectedIntakeKey)
        .map((programClass) => {
          const program = programById.get(String(programClass.program_id));
          if (!program) return null;
          return {
            ...program,
            intakeName: programClass.intake_name,
            intakeCode: programClass.intake_code,
            enrolledStudents: programClass.studentCount,
          };
        })
        .filter((program) => program && program.is_active !== false)
    : [];
  const programRows = [...(overview.intakes.length ? intakeProgramRows : programs)]
    .sort((a, b) => Number(b.enrolledStudents || 0) - Number(a.enrolledStudents || 0))
    .slice(0, 6);

  const metrics = [
    { label: "Students", value: students, icon: GraduationCap, tone: "bg-indigo-100 text-indigo-700" },
    { label: "Academic & support staff", value: staff, icon: Users, tone: "bg-blue-100 text-blue-700" },
    { label: "Active programmes", value: programs.length, icon: BookOpen, tone: "bg-emerald-100 text-emerald-700" },
    { label: "Faculties", value: overview.hierarchy?.faculties, icon: Building2, tone: "bg-violet-100 text-violet-700" },
    { label: "Departments", value: overview.hierarchy?.departments, icon: LayoutDashboard, tone: "bg-amber-100 text-amber-700" },
    { label: "Applications", value: applications, icon: ClipboardCheck, tone: "bg-rose-100 text-rose-700" },
  ];

  return (
    <AdminLayout title="Executive Portal">
      <div className="space-y-6">
        <section className="flex min-h-14 flex-wrap items-center gap-x-3 gap-y-1 border-b border-slate-200 pb-4">
          <h1 className="text-xl font-bold tracking-tight text-slate-950">{title}. {fullName}</h1>
          <span className="text-sm text-slate-400">·</span>
          <span className="text-sm font-medium text-slate-600">{periodLabel(activeYear)}</span>
          <span className="text-sm text-slate-400">·</span>
          <span className="text-sm font-medium text-slate-600">{periodLabel(activeSemester)}</span>
        </section>

        {notice && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {notice}
          </div>
        )}

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
          {metrics.map(({ label, value, icon: Icon, tone }) => (
            <div key={label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <span className={`grid h-10 w-10 place-items-center rounded-xl ${tone}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              </div>
              <strong className="mt-4 block text-2xl font-bold text-slate-950">
                {loading ? "…" : numberValue(value)}
              </strong>
              <span className="mt-1 block text-xs font-medium text-slate-500">{label}</span>
            </div>
          ))}
        </section>

        <section className="grid gap-6 lg:grid-cols-[1.35fr_0.65fr]">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-slate-950">Institutional pulse</h2>
                <p className="mt-1 text-sm text-slate-500">Current signals across the institution.</p>
              </div>
              <ShieldCheck className="h-5 w-5 text-indigo-600" />
            </div>
            <dl className="mt-5 grid gap-x-8 gap-y-4 sm:grid-cols-2">
              {[
                ["Active academic year", periodLabel(activeYear)],
                ["Active semester", periodLabel(activeSemester)],
                ["Sections", numberValue(overview.hierarchy?.sections)],
                ["Admission applications", numberValue(applications)],
                ["Academic staff", numberValue(users.academic)],
                ["Other staff", numberValue(users.staff)],
              ].map(([label, value]) => (
                <div key={label} className="flex items-center justify-between gap-4 border-b border-slate-100 pb-3 last:border-0">
                  <dt className="text-sm text-slate-500">{label}</dt>
                  <dd className="text-right text-sm font-semibold text-slate-900">{loading ? "…" : value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
            <h2 className="text-lg font-bold text-slate-950">Executive attention</h2>
            <p className="mt-1 text-sm text-slate-500">Areas ready for review or follow-up.</p>
            <div className="mt-5 space-y-3">
              {[
                ["Academic period", activeYear ? "Active" : "Needs setup", Boolean(activeYear)],
                ["Programme coverage", programs.length ? `${programs.length} active` : "Needs review", programs.length > 0],
                ["Institution hierarchy", overview.hierarchy?.departments ? "Available" : "Needs review", Boolean(overview.hierarchy?.departments)],
              ].map(([label, value, healthy]) => (
                <div key={label} className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-3">
                  <span className={`grid h-8 w-8 place-items-center rounded-full ${healthy ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {healthy ? <CheckCircle2 className="h-4 w-4" /> : <Activity className="h-4 w-4" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <strong className="block text-sm text-slate-900">{label}</strong>
                    <span className="text-xs text-slate-500">{value}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-slate-950">Academic footprint</h2>
                <p className="mt-1 text-sm text-slate-500">Active students grouped by programme for the selected intake.</p>
              </div>
              <Link to="/academic-management" className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 hover:underline">
                Open academic management <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <label htmlFor="executive-intake" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Intake
              </label>
              <select
                id="executive-intake"
                value={selectedIntakeKey}
                onChange={(event) => setSelectedIntakeKey(event.target.value)}
                disabled={!intakeOptions.length}
                className="min-w-[220px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-slate-50"
              >
                {intakeOptions.length ? (
                  intakeOptions.map((option) => (
                    <option key={option.key} value={option.key}>{option.label}</option>
                  ))
                ) : (
                  <option value="">No intakes configured</option>
                )}
              </select>
              {selectedIntake && (
                <span className="text-xs text-slate-500">Showing {selectedIntake.label}</span>
              )}
            </div>
            <div className="mt-5 overflow-x-auto">
              <table className="w-full min-w-[650px] text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-2 py-3 font-semibold">Programme</th>
                    <th className="px-2 py-3 font-semibold">Level</th>
                    <th className="px-2 py-3 font-semibold">Intake</th>
                    <th className="px-2 py-3 text-right font-semibold">Students</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {programRows.map((program) => (
                    <tr key={`${program.id || program.code}-${program.intakeCode || "all"}`}>
                      <td className="px-2 py-3 font-medium text-slate-900">{program.name || program.code || "Unnamed programme"}</td>
                      <td className="px-2 py-3 text-slate-500">{formatLevelLabel(program)}</td>
                      <td className="px-2 py-3 text-slate-500">{program.intakeName || program.intakeCode || selectedIntake?.label || "—"}</td>
                      <td className="px-2 py-3 text-right font-semibold text-slate-900">{numberValue(program.enrolledStudents)}</td>
                    </tr>
                  ))}
                  {!loading && !programRows.length && (
                    <tr><td colSpan="4" className="px-2 py-8 text-center text-sm text-slate-500">No programme data is available for this intake.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
            <h2 className="text-lg font-bold text-slate-950">Institution-wide review areas</h2>
            <p className="mt-1 text-sm text-slate-500">Open any area without leaving the executive workspace.</p>
            <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
              {reviewAreas.map(({ label, description, href, icon: Icon, tone }) => (
                <Link key={label} to={href} className="group flex items-center gap-3 rounded-xl border border-slate-100 p-3 transition-colors hover:border-indigo-200 hover:bg-indigo-50/40">
                  <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg border ${tone}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <strong className="block truncate text-sm text-slate-900">{label}</strong>
                    <small className="mt-0.5 block truncate text-xs text-slate-500">{description}</small>
                  </span>
                  <ArrowUpRight className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </AdminLayout>
  );
};

export default ExecutiveDashboard;
